import { useState, useEffect, useRef, useCallback } from "react";
import { RotateCcw, Play, ChevronsUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useHaptic } from "../../hooks/useHaptic";
import { useSound } from "../../hooks/useSound";
import Dice from "../common/Dice";

interface Props {
  dice: number[];
  holds: boolean[];
  rollCount: number;
  isMyTurn: boolean;
  isAdmin?: boolean;
  colorfulDice?: boolean;
  soundEnabled?: boolean;
  onRoll: () => void;
  onToggleHold: (index: number) => void;
}

export default function DiceTray({
  dice,
  holds,
  rollCount,
  isMyTurn,
  isAdmin = false,
  colorfulDice = false,
  soundEnabled = true,
  onRoll,
  onToggleHold,
}: Props) {
  const { t } = useTranslation();
  const { impact } = useHaptic();
  const { play } = useSound(soundEnabled);

  const [rollingStates, setRollingStates] = useState<boolean[]>([false, false, false, false, false]);
  const [animatedDice, setAnimatedDice] = useState<number[]>(dice);

  const lastRollCount = useRef(rollCount);
  const lastDiceStr = useRef(JSON.stringify(dice));

  // Swipe-to-throw gesture state
  const trayRef = useRef<HTMLDivElement>(null);
  const pointerStartY = useRef<number | null>(null);
  const pointerStartTime = useRef<number | null>(null);
  const pendingVelocityRef = useRef(0); // px/ms captured on swipe release
  const [swipeDelta, setSwipeDelta] = useState(0); // positive = swiped up
  const THROW_THRESHOLD = 55;
  const [threw, setThrew] = useState(false);

  const canInteract = isMyTurn || isAdmin;
  const canRoll = canInteract && rollCount < 3;
  const isAnyRolling = rollingStates.some((r) => r);

  // ── Roll animation engine ──────────────────────────────────────────────────
  const triggerStaggeredRoll = useCallback(() => {
    const velocity = pendingVelocityRef.current;
    pendingVelocityRef.current = 0;

    const newRolling = dice.map((_, i) =>
      rollCount === 1 ? true : !holds[i]
    );

    // Collect rolling indices in a random stop order for variety
    const rollingIndices = newRolling
      .map((r, i) => (r ? i : -1))
      .filter((i) => i >= 0)
      .sort(() => Math.random() - 0.5);

    const count = rollingIndices.length;

    // Velocity bonus: fast swipe → dramatically longer roll
    // Typical fast throw ~0.4–0.6 px/ms → velocity bonus; click → 0
    const velocityBonus = Math.min(velocity * 4000, 2000);
    const firstStop = 350 + velocityBonus * 0.1;    // first die stops quick
    const totalSpan = 1200 + velocityBonus;          // span from first to last

    setRollingStates(newRolling);
    if (newRolling.some((r) => r)) play("roll");

    rollingIndices.forEach((dieIdx, orderIdx) => {
      // Exponential curve: later dice get disproportionately more time
      const progress = count > 1 ? orderIdx / (count - 1) : 0;
      const duration = Math.min(firstStop + totalSpan * Math.pow(progress, 1.9), 3000);

      const startTime = Date.now();
      const interval = setInterval(() => {
        setAnimatedDice((prev) => {
          const next = [...prev];
          next[dieIdx] = Math.floor(Math.random() * 6) + 1;
          return next;
        });

        if (Date.now() - startTime >= duration) {
          clearInterval(interval);
          play("clink");
          setRollingStates((prev) => {
            const next = [...prev];
            next[dieIdx] = false;
            return next;
          });
          setAnimatedDice((prev) => {
            const next = [...prev];
            next[dieIdx] = dice[dieIdx];
            return next;
          });
          impact("medium");
        }
      }, 65);
    });
  }, [dice, holds, rollCount, play, impact]);

  useEffect(() => {
    const diceStr = JSON.stringify(dice);
    const wasNewRoll =
      rollCount > lastRollCount.current ||
      (rollCount > 0 && diceStr !== lastDiceStr.current);

    if (wasNewRoll) {
      triggerStaggeredRoll();
    } else if (!rollingStates.some((r) => r)) {
      setAnimatedDice(dice);
    }

    lastRollCount.current = rollCount;
    lastDiceStr.current = diceStr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dice, rollCount]);

  const handleRollClick = () => {
    if (isAnyRolling || !canRoll) return;
    onRoll();
  };

  // ── Swipe-to-throw gesture ─────────────────────────────────────────────────
  const onPointerDown = (e: React.PointerEvent) => {
    if (!canRoll || isAnyRolling) return;
    pointerStartY.current = e.clientY;
    pointerStartTime.current = Date.now();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (pointerStartY.current === null) return;
    const delta = pointerStartY.current - e.clientY; // positive = up
    setSwipeDelta(Math.max(0, Math.min(delta, 100)));
  };

  const onPointerUp = () => {
    if (pointerStartY.current === null) return;
    if (swipeDelta >= THROW_THRESHOLD && !isAnyRolling && canRoll) {
      const elapsed = Date.now() - (pointerStartTime.current ?? Date.now());
      const velocity = elapsed > 0 ? swipeDelta / elapsed : 0; // px/ms
      pendingVelocityRef.current = velocity;
      setThrew(true);
      setTimeout(() => setThrew(false), 500);
      impact("medium");
      onRoll();
    }
    pointerStartY.current = null;
    pointerStartTime.current = null;
    setSwipeDelta(0);
  };

  const onPointerCancel = () => {
    pointerStartY.current = null;
    pointerStartTime.current = null;
    setSwipeDelta(0);
  };

  // Tray perspective tilt based on swipe progress
  const swipeProgress = Math.min(swipeDelta / THROW_THRESHOLD, 1);
  const trayStyle: React.CSSProperties =
    swipeDelta > 0
      ? {
          transform: `perspective(600px) rotateX(${swipeProgress * 12}deg) translateY(${-swipeProgress * 10}px)`,
          transition: "none",
        }
      : { transform: "none", transition: "transform 0.3s ease" };

  // 2-1-2 layout
  const rows = [[0, 1], [2], [3, 4]];

  return (
    <div
      ref={trayRef}
      className="relative rounded-[2rem] overflow-hidden select-none"
      style={{
        background: "rgba(255,255,255,0.055)",
        backdropFilter: "blur(24px) saturate(160%)",
        WebkitBackdropFilter: "blur(24px) saturate(160%)",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow:
          "0 16px 48px rgba(0,0,0,0.55), 0 4px 12px rgba(0,0,0,0.4), inset 0 1.5px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.2)",
        touchAction: "none",
        ...trayStyle,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {/* Subtle top shine line */}
      <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none" />

      {/* My-turn ambient glow */}
      {isMyTurn && !isAnyRolling && (
        <div className="absolute inset-0 bg-blue-500/[0.04] pointer-events-none rounded-[2rem]" />
      )}

      <div className="p-5 pb-4">
        {/* Header row */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.28em] text-blue-300/80 mb-2">
              {t("game.diceTray", "Dice Tray")}
            </p>
            {/* Roll counter pips */}
            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-1.5 w-7 rounded-full transition-all duration-500"
                  style={{
                    background:
                      i < rollCount
                        ? "rgba(96,165,250,0.9)"
                        : "rgba(255,255,255,0.1)",
                    boxShadow:
                      i < rollCount
                        ? "0 0 8px rgba(96,165,250,0.6)"
                        : "none",
                  }}
                />
              ))}
            </div>
          </div>

          {canRoll && (
            <button
              onClick={handleRollClick}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={isAnyRolling}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-90 select-none"
              style={{
                background: isAnyRolling
                  ? "rgba(255,255,255,0.06)"
                  : "linear-gradient(135deg,rgba(59,130,246,0.85) 0%,rgba(99,102,241,0.85) 100%)",
                backdropFilter: "blur(8px)",
                border: isAnyRolling
                  ? "1px solid rgba(255,255,255,0.08)"
                  : "1px solid rgba(99,120,246,0.4)",
                boxShadow: isAnyRolling
                  ? "none"
                  : "0 4px 16px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.2)",
                color: isAnyRolling ? "rgba(255,255,255,0.3)" : "#fff",
              }}
            >
              {rollCount === 0 ? (
                <><Play size={15} fill="currentColor" />{t("game.roll", "Roll")}</>
              ) : (
                <><RotateCcw size={15} />{t("game.rollAgain", "Roll Again")}</>
              )}
            </button>
          )}
        </div>

        {/* 2-1-2 Dice grid */}
        <div className="flex flex-col gap-3 max-w-[300px] mx-auto">
          {rows.map((rowDice, rowIndex) => (
            <div
              key={rowIndex}
              className={`flex justify-center gap-4 ${rowIndex === 1 ? "scale-105" : ""}`}
            >
              {rowDice.map((dieIdx) => {
                const value = animatedDice[dieIdx];
                const isHeld = holds[dieIdx];
                const isRolling = rollingStates[dieIdx];
                const canSelect =
                  canInteract && rollCount > 0 && rollCount < 3 && !isAnyRolling;

                return (
                  <button
                    key={dieIdx}
                    onClick={() => canSelect && onToggleHold(dieIdx)}
                    onPointerDown={(e) => e.stopPropagation()}
                    disabled={!canSelect || isRolling}
                    className="relative rounded-[22%] transition-all duration-200 focus:outline-none"
                    style={{
                      cursor: canSelect ? "pointer" : "default",
                      transform: isHeld ? "scale(1.08)" : "scale(1)",
                      filter: isHeld
                        ? "drop-shadow(0 0 10px rgba(59,130,246,0.7))"
                        : "none",
                      zIndex: isHeld ? 10 : 1,
                    }}
                  >
                    {/* Held ring */}
                    {isHeld && (
                      <div
                        className="absolute inset-0 rounded-[22%] pointer-events-none"
                        style={{
                          boxShadow: "0 0 0 3px rgba(59,130,246,0.8)",
                          animation: "pulseGlow 2s ease-in-out infinite",
                        }}
                      />
                    )}

                    <Dice
                      value={value}
                      size="xl"
                      isRolling={isRolling}
                      isHeld={isHeld}
                      colorful={colorfulDice}
                      colorIndex={dieIdx}
                      className={threw && !isHeld ? "dice-throw-arc" : ""}
                    />

                    {/* Keep / Rethrow label */}
                    {rollCount > 0 && !isRolling && (
                      <div
                        className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wide transition-all pointer-events-none"
                        style={{
                          background: isHeld
                            ? "rgba(59,130,246,0.92)"
                            : "rgba(10,12,40,0.62)",
                          border: isHeld
                            ? "1px solid rgba(120,170,255,0.55)"
                            : "1px solid rgba(255,255,255,0.15)",
                          color: isHeld ? "#fff" : "rgba(255,255,255,0.85)",
                          backdropFilter: "blur(6px)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isHeld ? t("game.keep") : t("game.rethrow")}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Swipe-to-throw zone */}
        {canRoll && !isAnyRolling && (
          <div className="mt-6 flex flex-col items-center gap-1.5">
            {swipeDelta > 10 ? (
              <p
                className="text-[11px] font-bold uppercase tracking-widest transition-all"
                style={{
                  color:
                    swipeDelta >= THROW_THRESHOLD
                      ? "rgba(96,165,250,1)"
                      : "rgba(255,255,255,0.4)",
                  textShadow:
                    swipeDelta >= THROW_THRESHOLD
                      ? "0 0 12px rgba(96,165,250,0.8)"
                      : "none",
                }}
              >
                {swipeDelta >= THROW_THRESHOLD
                  ? "🎲 " + t("game.release", "Release!")
                  : t("game.keepSwiping", "Keep going...")}
              </p>
            ) : rollCount > 0 ? (
              <p className="text-[10px] text-ink/30 font-semibold uppercase tracking-widest">
                {t("game.selectHint", "Tap to keep · Swipe up to throw")}
              </p>
            ) : (
              <div className="flex flex-col items-center gap-0.5 opacity-40">
                <ChevronsUp size={18} className="swipe-hint text-blue-300" />
                <p className="text-[10px] font-semibold uppercase tracking-widest text-ink/50">
                  {t("game.swipeToThrow", "Swipe up to throw")}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Waiting state */}
        {!canInteract && (
          <div className="mt-6 flex items-center justify-center gap-2 text-ink/30">
            <div
              className="w-1.5 h-1.5 rounded-full animate-ping"
              style={{ background: "rgba(96,165,250,0.5)" }}
            />
            <p className="text-[10px] font-bold uppercase tracking-[0.2em]">
              {t("game.waitingForTurn", "Waiting for other player…")}
            </p>
            <div
              className="w-1.5 h-1.5 rounded-full animate-ping"
              style={{ background: "rgba(96,165,250,0.5)", animationDelay: "0.3s" }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
